import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Utensils,
  Image as ImageIcon,
  ShoppingBasket,
  ExternalLink,
  Store,
  Minus,
} from 'lucide-react';
import {
  getRestaurantDishes,
  createDish,
  updateDish,
  deleteDish,
  formatCurrency,
  handleRestaurantApiError,
  getCategories,
  getManagementGroceryShop,
  getManagementGroceryShopProducts,
  createManagementGroceryShop,
  updateManagementGroceryShop,
  createManagementGroceryShopProduct,
  updateManagementGroceryShopProduct,
  deleteManagementGroceryShopProduct,
} from '../../../services/restaurantApi';
import SkeletonImage from '../../skeletons/SkeletonImage';
import { toastSuccess, toastError } from '../../../utils/toast';
import DishModal from '../modals/DishModal';
import GroceryShopProductModal from '../modals/GroceryShopProductModal';
import GroceryShopFormModal from '../modals/GroceryShopFormModal';
import { PageSkeleton } from '../DashboardLoader';

const LOW_STOCK_THRESHOLD = 5;

const PRIMARY = '#054625';
const TERRA = '#EF4C23';

const formatCatalogPrice = (p) => {
  const n = typeof p === 'string' ? parseFloat(p) : Number(p);
  if (Number.isNaN(n)) return '—';
  return `${n.toFixed(2).replace('.', ',')} €`;
};

/**
 * Grocery shop (management) : catalogue plateforme lié au restaurant + produits menu tag épicerie.
 */
const RestaurantGrocery = () => {
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [categories, setCategories] = useState([]);

  const [catalogProducts, setCatalogProducts] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [linkedShop, setLinkedShop] = useState(null);
  const [showShopForm, setShowShopForm] = useState(false);
  const [shopFormSaving, setShopFormSaving] = useState(false);
  const [showCatalogProductModal, setShowCatalogProductModal] = useState(false);
  const [selectedCatalogProduct, setSelectedCatalogProduct] = useState(null);

  const [activeTab, setActiveTab] = useState('catalog');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const loadData = async () => {
    try {
      setLoading(true);
      setCatalogLoading(true);
      const [categoriesData, dishesData] = await Promise.all([
        getCategories(),
        getRestaurantDishes(),
      ]);
      const validCategories = Array.isArray(categoriesData)
        ? categoriesData.filter((cat) => cat && typeof cat === 'object' && cat.id && cat.name)
        : [];
      setCategories(validCategories);
      const validDishes = Array.isArray(dishesData)
        ? dishesData.filter((d) => d && typeof d === 'object' && d.id && d.name)
        : [];
      setDishes(validDishes);

      try {
        const shop = await getManagementGroceryShop();
        setLinkedShop(shop && typeof shop === 'object' ? shop : null);
        if (shop?.id) {
          const products = await getManagementGroceryShopProducts({
            limit: 500,
            active_only: false,
          });
          setCatalogProducts(Array.isArray(products) ? products : []);
        } else {
          setCatalogProducts([]);
        }
      } catch (e) {
        console.error('Catalogue grocery shop:', e);
        setLinkedShop(null);
        setCatalogProducts([]);
      } finally {
        setCatalogLoading(false);
      }
    } catch (err) {
      console.error(err);
      toastError(handleRestaurantApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const groceryDishes = useMemo(
    () => dishes.filter((d) => d.is_grocery_product),
    [dishes],
  );

  const filteredDishes = useMemo(() => {
    if (!searchTerm.trim()) return groceryDishes;
    const q = searchTerm.toLowerCase();
    return groceryDishes.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) || d.description?.toLowerCase().includes(q),
    );
  }, [groceryDishes, searchTerm]);

  const catalogCategories = useMemo(() => {
    const set = new Set();
    catalogProducts.forEach((p) => {
      if (p.category) set.add(p.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [catalogProducts]);

  const catalogStats = useMemo(() => {
    const total = catalogProducts.length;
    const active = catalogProducts.filter((p) => p.is_active !== false).length;
    const inactive = total - active;
    const lowStock = catalogProducts.filter(
      (p) => p.stock_quantity != null && p.stock_quantity <= LOW_STOCK_THRESHOLD,
    ).length;
    return { total, active, inactive, lowStock };
  }, [catalogProducts]);

  const filteredCatalog = useMemo(() => {
    let list = catalogProducts;
    if (statusFilter === 'active') list = list.filter((p) => p.is_active !== false);
    if (statusFilter === 'inactive') list = list.filter((p) => p.is_active === false);
    if (categoryFilter !== 'all') list = list.filter((p) => p.category === categoryFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (p) =>
          p.name?.toLowerCase().includes(q) ||
          (p.description && String(p.description).toLowerCase().includes(q)) ||
          (p.category && String(p.category).toLowerCase().includes(q)),
      );
    }
    return list;
  }, [catalogProducts, searchTerm, statusFilter, categoryFilter]);

  const handleCreate = async (dishData) => {
    try {
      const apiDishData = {
        name: dishData.name,
        description: dishData.description,
        price: dishData.price,
        category_id: dishData.category_id,
        dish_image_url:
          dishData.image_url ||
          'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
        is_available: dishData.is_available !== false,
        hidden_from_marketplace: !!dishData.hidden_from_marketplace,
        is_grocery_product: true,
      };
      const newDish = await createDish(apiDishData);
      setDishes((prev) => [...prev, newDish]);
      setShowModal(false);
      toastSuccess('Produit épicerie créé.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleUpdate = async (dishId, dishData) => {
    try {
      const apiDishData = {
        name: dishData.name,
        description: dishData.description,
        price: dishData.price,
        category_id: dishData.category_id,
        dish_image_url:
          dishData.image_url ||
          'https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop',
        is_available: dishData.is_available !== false,
        hidden_from_marketplace: !!dishData.hidden_from_marketplace,
        is_grocery_product: true,
      };
      const updated = await updateDish(dishId, apiDishData);
      setDishes((prev) => prev.map((d) => (d.id === dishId ? updated : d)));
      setSelectedDish(null);
      toastSuccess('Produit mis à jour.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleDelete = async (dishId) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await deleteDish(dishId);
      setDishes((prev) => prev.filter((d) => d.id !== dishId));
      toastSuccess('Produit supprimé.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleEditCatalogProduct = (product) => {
    setSelectedCatalogProduct(product);
    setShowCatalogProductModal(true);
  };

  const handleCreateCatalogProduct = async (productData) => {
    try {
      const created = await createManagementGroceryShopProduct(productData);
      setCatalogProducts((prev) => [...prev, created]);
      setShowCatalogProductModal(false);
      toastSuccess('Produit catalogue créé.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleUpdateCatalogProduct = async (productId, productData) => {
    try {
      const updated = await updateManagementGroceryShopProduct(productId, productData);
      setCatalogProducts((prev) => prev.map((p) => (p.id === productId ? updated : p)));
      setSelectedCatalogProduct(null);
      setShowCatalogProductModal(false);
      toastSuccess('Produit catalogue mis à jour.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleAdjustStock = async (product, delta) => {
    const current = product.stock_quantity != null ? product.stock_quantity : 0;
    const next = Math.max(0, current + delta);
    if (next === current) return;
    try {
      const updated = await updateManagementGroceryShopProduct(product.id, { stock_quantity: next });
      setCatalogProducts((prev) => prev.map((p) => (p.id === product.id ? updated : p)));
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleDeleteCatalogProduct = async (productId) => {
    if (!window.confirm('Supprimer ce produit du catalogue ?')) return;
    try {
      await deleteManagementGroceryShopProduct(productId);
      setCatalogProducts((prev) => prev.filter((p) => p.id !== productId));
      toastSuccess('Produit supprimé du catalogue.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    }
  };

  const handleSubmitShopForm = async (shopData) => {
    setShopFormSaving(true);
    try {
      const saved = linkedShop
        ? await updateManagementGroceryShop(shopData)
        : await createManagementGroceryShop(shopData);
      setLinkedShop(saved);
      setShowShopForm(false);
      toastSuccess(linkedShop ? 'Magasin mis à jour.' : 'Magasin créé.');
    } catch (err) {
      toastError(handleRestaurantApiError(err));
    } finally {
      setShopFormSaving(false);
    }
  };

  if (loading) return <PageSkeleton />;

  const TABS = [
    { id: 'catalog', label: 'Catalogue grocery shop', count: catalogStats.total },
    { id: 'dishes', label: 'Produits « épicerie » (menu)', count: groceryDishes.length },
  ];

  return (
    <div style={{ padding: 'clamp(20px, 2.6vw, 34px) clamp(18px, 2.6vw, 36px) clamp(36px, 4vw, 60px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 24px)' }}>
      {/* En-tête */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: TERRA }}>
            {catalogStats.total} produits catalogue · {groceryDishes.length} produits menu
          </span>
          <h1 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: 'clamp(26px, 3vw, 38px)', lineHeight: 1, letterSpacing: '-0.025em' }}>Grocery shop</h1>
          <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.68)', maxWidth: '52ch' }}>
            Catalogue plateforme lié à votre restaurant et produits « épicerie » créés depuis votre menu.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { setSelectedDish(null); setShowModal(true); }}
          className="inline-flex items-center gap-2"
          style={{ border: 'none', cursor: 'pointer', background: TERRA, color: '#FCFBF5', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 20px', borderRadius: '999px' }}
        >
          <Plus size={16} />
          Créer un produit épicerie
        </button>
      </div>

      {/* KPI catalogue */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))', gap: 'clamp(12px, 1.6vw, 18px)' }}>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Produits catalogue</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{catalogStats.total}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Actifs</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em', color: PRIMARY }}>{catalogStats.active}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Inactifs</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em' }}>{catalogStats.inactive}</strong>
        </div>
        <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.55)' }}>Stock bas (≤ {LOW_STOCK_THRESHOLD})</span>
          <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '28px', letterSpacing: '-0.02em', color: TERRA }}>{catalogStats.lowStock}</strong>
        </div>
      </div>

      {/* Onglets */}
      <div className="flex flex-wrap gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px', width: 'fit-content' }}>
        {TABS.map((t) => {
          const active = activeTab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className="flex items-center gap-2"
              style={{
                border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13.5px', fontWeight: 600,
                padding: '9px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
                background: active ? PRIMARY : 'transparent',
                color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
              }}
            >
              {t.label}
              <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11px', fontWeight: 700, color: active ? '#FFC717' : 'rgba(5,16,4,0.4)' }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* Recherche + filtres */}
      <div className="flex flex-col sm:flex-row gap-3">
        <label className="flex items-center gap-2.5 flex-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '0 18px', minHeight: '44px' }}>
          <Search size={15} style={{ color: PRIMARY, flexShrink: 0 }} />
          <input
            type="search"
            placeholder={activeTab === 'catalog' ? 'Rechercher dans le catalogue…' : 'Rechercher dans vos produits menu…'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 0, border: 'none', background: 'transparent', fontSize: '14px', color: '#051004', outline: 'none' }}
          />
        </label>
        {activeTab === 'catalog' && (
          <>
            <div className="flex gap-1" style={{ background: '#FCFBF5', borderRadius: '999px', padding: '4px' }}>
              {[
                { id: 'all', label: 'Tous' },
                { id: 'active', label: 'Actifs' },
                { id: 'inactive', label: 'Inactifs' },
              ].map((s) => {
                const active = statusFilter === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStatusFilter(s.id)}
                    style={{
                      border: 'none', cursor: 'pointer', fontFamily: 'Poppins, sans-serif', fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap',
                      padding: '8px 16px', borderRadius: '999px',
                      background: active ? PRIMARY : 'transparent', color: active ? '#FBF9D9' : 'rgba(5,16,4,0.66)',
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              style={{ background: '#FCFBF5', border: 'none', borderRadius: '999px', padding: '0 18px', minHeight: '44px', fontSize: '14px', color: '#051004', outline: 'none' }}
            >
              <option value="all">Toutes les catégories</option>
              {catalogCategories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Catalogue grocery shop */}
      {activeTab === 'catalog' && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-2">
              <Store size={20} style={{ color: TERRA }} />
              <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Catalogue</h2>
            </span>
            {linkedShop && !catalogLoading && (
              <span className="flex items-center gap-2" style={{ marginLeft: 'auto' }}>
                <button
                  type="button"
                  onClick={() => setShowShopForm(true)}
                  className="inline-flex items-center gap-1.5"
                  style={{ border: '1px solid rgba(5,16,4,0.16)', cursor: 'pointer', background: 'transparent', color: PRIMARY, fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 16px', borderRadius: '999px' }}
                >
                  <Edit size={14} />
                  Modifier le magasin
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedCatalogProduct(null); setShowCatalogProductModal(true); }}
                  className="inline-flex items-center gap-1.5"
                  style={{ border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '13px', minHeight: '38px', padding: '0 16px', borderRadius: '999px' }}
                >
                  <Plus size={14} />
                  Ajouter un produit
                </button>
              </span>
            )}
          </div>

          {catalogLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ height: '80px', borderRadius: '24px', background: '#FCFBF5' }} className="animate-pulse" />
              ))}
            </div>
          ) : !linkedShop ? (
            <div style={{ background: '#FCFBF5', border: '1px dashed rgba(239,76,35,0.35)', borderRadius: '26px', padding: '32px 24px', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Vous n'avez pas encore de magasin catalogue.</p>
              <p style={{ margin: '8px 0 0', fontSize: '13.5px', color: 'rgba(5,16,4,0.66)' }}>
                Créez votre magasin pour vendre vos produits sur le catalogue plateforme.
              </p>
              <button
                type="button"
                onClick={() => setShowShopForm(true)}
                className="inline-flex items-center gap-2"
                style={{ marginTop: '16px', border: 'none', cursor: 'pointer', background: PRIMARY, color: '#FBF9D9', fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '14px', minHeight: '44px', padding: '0 22px', borderRadius: '999px' }}
              >
                <Plus size={16} />
                Créer mon magasin
              </button>
            </div>
          ) : filteredCatalog.length === 0 ? (
            <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '48px 16px', textAlign: 'center' }}>
              <ShoppingBasket size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>
                {catalogProducts.length === 0
                  ? 'Ce catalogue ne contient encore aucun produit actif.'
                  : 'Aucun résultat pour cette recherche ou ces filtres.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredCatalog.map((p) => {
                const stock = p.stock_quantity ?? 0;
                const stockColor = stock <= 0 ? '#A32C0F' : stock <= LOW_STOCK_THRESHOLD ? '#A32C0F' : '#051004';
                return (
                  <div key={p.id} className="flex items-center gap-3.5 flex-wrap" style={{ background: '#FCFBF5', borderRadius: '26px', padding: '14px' }}>
                    <span style={{ flex: 'none', width: '66px', height: '66px', borderRadius: '18px', overflow: 'hidden', display: 'grid', placeItems: 'center', background: '#F4F1E2', fontSize: '24px' }}>
                      {p.image_url ? <img src={p.image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" /> : <span aria-hidden="true">🛒</span>}
                    </span>
                    <span style={{ flex: '1 1 200px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="flex items-baseline gap-2.5 flex-wrap">
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }} className="truncate">{p.name}</strong>
                        {p.category && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FBF9D9', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{p.category}</span>}
                        {p.is_active === false && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FEF0EA', color: '#A32C0F', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Inactif</span>}
                      </span>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.68)' }}>{formatCatalogPrice(p.price)}</span>
                    </span>

                    <span className="flex items-center gap-2" style={{ background: '#FBF9D9', borderRadius: '999px', padding: '4px', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        onClick={() => handleAdjustStock(p, -1)}
                        disabled={stock <= 0}
                        aria-label="Retirer une unité"
                        style={{ border: 'none', cursor: stock <= 0 ? 'default' : 'pointer', width: '30px', height: '30px', borderRadius: '999px', background: '#FCFBF5', color: PRIMARY, display: 'grid', placeItems: 'center', opacity: stock <= 0 ? 0.4 : 1 }}
                      >
                        <Minus size={14} />
                      </button>
                      <span style={{ minWidth: '28px', textAlign: 'center', fontWeight: 700, fontSize: '14px', color: stockColor }}>{stock}</span>
                      <button
                        type="button"
                        onClick={() => handleAdjustStock(p, 1)}
                        aria-label="Ajouter une unité"
                        style={{ border: 'none', cursor: 'pointer', width: '30px', height: '30px', borderRadius: '999px', background: PRIMARY, color: '#FBF9D9', display: 'grid', placeItems: 'center' }}
                      >
                        <Plus size={14} />
                      </button>
                    </span>

                    <span className="flex items-center gap-1.5">
                      <Link
                        to={`/grocery-shop-product/${p.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 w-9 h-9 rounded-full"
                        style={{ justifyContent: 'center', color: 'rgba(5,16,4,0.6)' }}
                        title="Voir la fiche publique"
                      >
                        <ExternalLink size={16} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleEditCatalogProduct(p)}
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                        title="Modifier"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteCatalogProduct(p.id)}
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {linkedShop && !catalogLoading && (
            <p style={{ margin: 0, fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.55)' }}>
              Magasin : <strong>{linkedShop.name}</strong>{linkedShop.description ? ` — ${linkedShop.description}` : ''}
            </p>
          )}
        </section>
      )}

      {/* Produits menu (tag épicerie) */}
      {activeTab === 'dishes' && (
        <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div className="flex items-center gap-2">
            <Utensils size={20} style={{ color: PRIMARY }} />
            <h2 style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 900, fontSize: '22px', letterSpacing: '-0.02em' }}>Produits « épicerie » (menu)</h2>
          </div>
          <p style={{ margin: 0, fontSize: '13.5px', color: 'rgba(5,16,4,0.6)' }}>
            Même système que l'onglet Menu, avec le tag produit d'épicerie. Visibles sur le marketplace sauf si masqués.
          </p>

          {filteredDishes.length === 0 ? (
            <div style={{ background: '#FCFBF5', borderRadius: '26px', padding: '56px 16px', textAlign: 'center' }}>
              <ShoppingBasket size={40} style={{ color: 'rgba(5,16,4,0.18)', margin: '0 auto 14px' }} />
              <p style={{ margin: 0, fontSize: '14px', color: 'rgba(5,16,4,0.55)' }}>
                {groceryDishes.length === 0
                  ? "Aucun produit menu tagué épicerie. Utilisez le bouton ci-dessus ou cochez « Produit d'épicerie » sur un plat dans l'onglet Menu."
                  : 'Aucun résultat pour cette recherche.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredDishes.map((dish) => {
                const dishImg = dish.dish_image_url || dish.image_url;
                const categoryName = typeof dish.category === 'object' && dish.category?.name ? dish.category.name : (dish.category || '—');
                return (
                  <div key={dish.id} className="flex items-center gap-3.5 flex-wrap" style={{ background: '#FCFBF5', borderRadius: '26px', padding: '14px' }}>
                    <span style={{ flex: 'none', width: '66px', height: '66px', borderRadius: '18px', overflow: 'hidden', display: 'block', background: '#F4F1E2', position: 'relative' }}>
                      {dishImg ? (
                        <SkeletonImage src={dishImg} alt={dish.name} width="w-full" height="h-full" className="rounded-none" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <ImageIcon className="h-6 w-6" style={{ color: 'rgba(5,70,37,0.25)' }} />
                        </div>
                      )}
                    </span>
                    <span style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="flex items-baseline gap-2.5 flex-wrap">
                        <strong style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: '17px' }} className="truncate">{dish.name}</strong>
                        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#FBF9D9', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>{categoryName}</span>
                        {dish.hidden_from_marketplace && <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '11.5px', background: '#F4F1E2', color: '#393A30', padding: '4px 11px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Masqué marketplace</span>}
                      </span>
                      <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13px', color: 'rgba(5,16,4,0.68)' }} className="truncate">{dish.description || 'Sans description'}</span>
                    </span>
                    <strong style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>{formatCurrency(dish.price)}</strong>
                    <span className="flex items-center gap-1.5">
                      <Link
                        to={`/product/${dish.id}`}
                        className="inline-flex items-center gap-1 w-9 h-9 rounded-full"
                        style={{ justifyContent: 'center', color: 'rgba(5,16,4,0.6)' }}
                        title="Voir les détails"
                      >
                        <ExternalLink size={16} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => setSelectedDish(dish)}
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                        title="Modifier"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(dish.id)}
                        className="w-9 h-9 rounded-full flex items-center justify-center"
                        style={{ border: 'none', cursor: 'pointer', background: 'transparent', color: 'rgba(5,16,4,0.6)' }}
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      <DishModal
        isOpen={showModal || !!selectedDish}
        groceryShopMode
        onClose={() => {
          setShowModal(false);
          setSelectedDish(null);
        }}
        dish={selectedDish}
        onSuccess={selectedDish ? handleUpdate : handleCreate}
        categories={categories}
      />

      <GroceryShopProductModal
        isOpen={showCatalogProductModal}
        onClose={() => {
          setShowCatalogProductModal(false);
          setSelectedCatalogProduct(null);
        }}
        product={selectedCatalogProduct}
        onSuccess={selectedCatalogProduct ? handleUpdateCatalogProduct : handleCreateCatalogProduct}
      />

      <GroceryShopFormModal
        isOpen={showShopForm}
        onClose={() => setShowShopForm(false)}
        shop={linkedShop}
        onSubmit={handleSubmitShopForm}
        saving={shopFormSaving}
      />
    </div>
  );
};

export default RestaurantGrocery;
